import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AttendanceService } from '../../services/attendance.service';
import { EmployeeResponseDto, UpdateEmployeeCommand, CreateEmployeeCommand } from '../../interfaces/interfaces';

@Component({
  selector: 'app-employee-management',
  templateUrl: './employee-management.component.html',
  styleUrls: ['./employee-management.component.scss']
})
export class EmployeeManagementComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'id', 'photo', 'fullName', 'personId', 'fingerprintId', 'salaryType', 
    'hourlyRate', 'monthlySalary', 'isActive', 'actions'
  ];
  dataSource = new MatTableDataSource<EmployeeResponseDto>();
  
  employeeForm: FormGroup;
  isFormVisible = false;
  isEditMode = false;
  selectedEmployee: EmployeeResponseDto | null = null;
  loading = false;
  selectedPhoto: string | null = null;
  photoPreview: string | null = null;

  salaryTypes = [
    { value: 1, label: 'Hourly' },
    { value: 2, label: 'Monthly' }
  ];

  constructor(
    private attendanceService: AttendanceService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.employeeForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      personId: ['', [Validators.required, Validators.minLength(3)]],
      fingerprintId: [''],
      hourlyRate: [0, [Validators.required, Validators.min(0)]],
      monthlySalary: [0, [Validators.required, Validators.min(0)]],
      salaryType: [1, Validators.required],
      isActive: [true],
      photo: ['']
    });
  }

  ngOnInit(): void {
    this.loadEmployees();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadEmployees(): void {
    this.loading = true;
    this.attendanceService.getAllEmployees().subscribe({
      next: (response) => {
        this.dataSource.data = response.employees;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  showAddForm(): void {
    this.isEditMode = false;
    this.selectedEmployee = null;
    this.selectedPhoto = null;
    this.photoPreview = null;
    this.employeeForm.reset({
      salaryType: 1,
      isActive: true,
      hourlyRate: 0,
      monthlySalary: 0
    });
    this.isFormVisible = true;
  }

  showEditForm(employee: EmployeeResponseDto): void {
    this.isEditMode = true;
    this.selectedEmployee = employee;
    this.selectedPhoto = employee.photo || null;
    this.photoPreview = employee.photo || null;
    this.employeeForm.patchValue({
      firstName: employee.firstName,
      lastName: employee.lastName,
      personId: employee.personId,
      fingerprintId: employee.fingerprintId || '',
      hourlyRate: employee.hourlyRate,
      monthlySalary: employee.monthlySalary,
      salaryType: employee.salaryType,
      isActive: employee.isActive,
      photo: employee.photo || ''
    });
    this.isFormVisible = true;
  }

  hideForm(): void {
    this.isFormVisible = false;
    this.selectedPhoto = null;
    this.photoPreview = null;
    this.employeeForm.reset();
  }

  onPhotoSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        this.snackBar.open('Photo size must be less than 5MB', 'Close', { duration: 3000 });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedPhoto = e.target.result;
        this.photoPreview = e.target.result;
        this.employeeForm.patchValue({ photo: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }

  removePhoto(): void {
    this.selectedPhoto = null;
    this.photoPreview = null;
    this.employeeForm.patchValue({ photo: '' });
  }

  onSubmit(): void {
    if (this.employeeForm.valid) {
      const formValue = this.employeeForm.value;
      
      if (this.isEditMode && this.selectedEmployee) {
        const updateCommand: UpdateEmployeeCommand = {
          id: this.selectedEmployee.id,
          ...formValue,
          photo: this.selectedPhoto || undefined
        };
        
        this.attendanceService.updateEmployee(updateCommand).subscribe({
          next: () => {
            this.snackBar.open('Employee updated successfully', 'Close', { duration: 3000 });
            this.hideForm();
            this.loadEmployees();
          },
          error: (error) => {
            console.error('Error updating employee:', error);
            this.snackBar.open('Error updating employee', 'Close', { duration: 3000 });
          }
        });
      } else {
        const createCommand: CreateEmployeeCommand = {
          ...formValue,
          photo: this.selectedPhoto || undefined
        };
        
        this.attendanceService.createEmployee(createCommand).subscribe({
          next: () => {
            this.snackBar.open('Employee created successfully', 'Close', { duration: 3000 });
            this.hideForm();
            this.loadEmployees();
          },
          error: (error) => {
            console.error('Error creating employee:', error);
            this.snackBar.open('Error creating employee', 'Close', { duration: 3000 });
          }
        });
      }
    }
  }

  getSalaryTypeLabel(salaryType: number): string {
    return this.salaryTypes.find(st => st.value === salaryType)?.label || 'Unknown';
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  getSalaryDisplay(employee: EmployeeResponseDto): string {
    if (employee.salaryType === 1) {
      return `$${employee.hourlyRate}/hr`;
    } else {
      return `$${employee.monthlySalary}/month`;
    }
  }
} 